import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { API_CONFIG } from '../../config/api.config';
import DocumentDetail from '../detail/detail';
import Auth from '../auth/auth';
import Profile from '../profile/profile';
import {
  Compass,
  Search,
  Plus,
  LayoutGrid,
  Settings,
  Bell,
  Menu,
  ChevronRight,
  MoreVertical,
  X,
  Rss,
  LogIn,
  LogOut,
  User,
  HelpCircle,
  RotateCw,
  ChevronDown,
  Copy,
  Trash2,
  Bookmark,
  UserCircle,
  Circle,
  CircleCheck,
  Filter,
  FunnelPlus
} from 'lucide-react';
import Avatar from '../common/Avatar';
import FeedLogo from '../common/FeedLogo';

/**
 * 将 HTML 转换为纯文本并限制字数
 * @param {string} html - HTML 字符串
 * @param {number} maxLength - 最大字符数
 * @returns {string} 纯文本
 */
const stripHtmlAndTruncate = (html, maxLength = 200) => {
  if (!html) return '';

  // 创建临时元素来解析 HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // 清理多余空格和换行
  text = text.replace(/\s+/g, ' ').trim();

  // 限制字符数
  if (text.length > maxLength) {
    text = text.slice(0, maxLength) + '...';
  }

  return text;
};

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [feedItems, setFeedItems] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [selectedFeedId, setSelectedFeedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [isGridView, setIsGridView] = useState(() => {
    const saved = localStorage.getItem('isGridView');
    return saved !== null ? saved === 'true' : false;
  });
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showFeedMenu, setShowFeedMenu] = useState(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const [showingFavorites, setShowingFavorites] = useState(false);

  // 搜索相关状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [searchPage, setSearchPage] = useState(1);

  // 探索页面相关状态
  const [exploreKeyword, setExploreKeyword] = useState('');
  const [exploreResults, setExploreResults] = useState([]);
  const [exploreLoading, setExploreLoading] = useState(false);
  const [explorePage, setExplorePage] = useState(1);
  const [exploreHasMore, setExploreHasMore] = useState(true);
  const [exploreTotal, setExploreTotal] = useState(0);

  // 热门文档相关状态
  const [hotDocuments, setHotDocuments] = useState([]);
  const [hotLoading, setHotLoading] = useState(true);
  const [hotPage, setHotPage] = useState(1);
  const [hotHasMore, setHotHasMore] = useState(true);
  const [hotTotal, setHotTotal] = useState(0);

  // 漏斗相关状态
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);
  const [showAddFunnelModal, setShowAddFunnelModal] = useState(false);
  const [funnelName, setFunnelName] = useState('');
  const [funnelIncludeKeywords, setFunnelIncludeKeywords] = useState('');
  const [funnelExcludeKeywords, setFunnelExcludeKeywords] = useState('');
  const [funnelSelectedFeeds, setFunnelSelectedFeeds] = useState([]);
  const [addingFunnel, setAddingFunnel] = useState(false);
  const [funnelError, setFunnelError] = useState('');
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [showingFunnel, setShowingFunnel] = useState(false);
  const [showDeleteFunnelConfirm, setShowDeleteFunnelConfirm] = useState(false);
  const [deletingFunnelId, setDeletingFunnelId] = useState(null);
  const [showEditFunnelModal, setShowEditFunnelModal] = useState(false);
  const [editingFunnelId, setEditingFunnelId] = useState(null);
  const [showFunnelMenu, setShowFunnelMenu] = useState(false);

  // Add Feed 弹窗相关状态
  const [showAddFeedModal, setShowAddFeedModal] = useState(false);
  const [feedLink, setFeedLink] = useState('');
  const [addingFeed, setAddingFeed] = useState(false);
  const [feedError, setFeedError] = useState('');

  // 设置弹窗相关状态
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // 从 localStorage 读取翻译设置，默认为 true
  const [showTranslation, setShowTranslation] = useState(() => {
    const saved = localStorage.getItem('showTranslation');
    return saved !== null ? saved === 'true' : true;
  });
  // 从 localStorage 读取自动翻页设置，默认为 true
  const [autoScrollLoad, setAutoScrollLoad] = useState(() => {
    const saved = localStorage.getItem('autoScrollLoad');
    return saved !== null ? saved === 'true' : true;
  });

  // 切换翻译设置
  const handleToggleTranslation = () => {
    const newValue = !showTranslation;
    setShowTranslation(newValue);
    localStorage.setItem('showTranslation', newValue.toString());
    // 重新加载数据
    setCurrentPage(1);
    fetchFeedItems(1, false, selectedFeedId);
  };

  // 切换自动翻页设置
  const handleToggleAutoScrollLoad = () => {
    const newValue = !autoScrollLoad;
    setAutoScrollLoad(newValue);
    localStorage.setItem('autoScrollLoad', newValue.toString());
  };

  // 获取当前登录用户
  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  };
  const currentUser = getCurrentUser();

  // 处理登出
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // 从 API 获取漏斗列表
  const fetchFunnels = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      setFunnelsLoading(true);
      const response = await apiClient.get('/funnel/list');
      if (response.code === 0) {
        setFunnels(response.data || []);
      } else {
        console.error('获取漏斗列表失败:', response.message);
      }
    } catch (err) {
      console.error('获取漏斗列表失败:', err);
    } finally {
      setFunnelsLoading(false);
    }
  };

  // 添加漏斗
  const addFunnel = async () => {
    if (!funnelName.trim()) {
      setFunnelError('请输入漏斗名称');
      return;
    }

    setAddingFunnel(true);
    setFunnelError('');

    try {
      const response = await apiClient.post('/funnel/add', {
        name: funnelName.trim(),
        include_keywords: funnelIncludeKeywords.trim(),
        exclude_keywords: funnelExcludeKeywords.trim(),
        feed_ids: funnelSelectedFeeds
      });

      if (response.code === 0) {
        // 添加成功，刷新漏斗列表
        fetchFunnels();
        handleCloseAddFunnelModal();
      } else {
        setFunnelError(response.message || '添加失败');
      }
    } catch (err) {
      console.error('添加漏斗失败:', err);
      setFunnelError('添加失败，请稍后重试');
    } finally {
      setAddingFunnel(false);
    }
  };

  // 显示删除漏斗确认框
  const handleDeleteFunnel = (funnelId) => {
    setDeletingFunnelId(funnelId);
    setShowDeleteFunnelConfirm(true);
  };

  // 确认删除漏斗
  const confirmDeleteFunnel = async () => {
    if (deletingFunnelId) {
      try {
        const response = await apiClient.post('/funnel/delete', {
          funnel_id: deletingFunnelId
        });

        if (response.code === 0) {
          // 删除成功，如果删除的是当前选中的漏斗，则清除选中状态
          if (selectedFunnelId === deletingFunnelId) {
            setSelectedFunnelId(null);
            setShowingFunnel(false);
            setShowSearchResult(false);
            setSearchKeyword('');
            fetchFeedItems(1, false, null);
          }
          // 刷新漏斗列表
          fetchFunnels();
        } else {
          setError(response.message || '删除失败');
        }
      } catch (err) {
        console.error('删除漏斗失败:', err);
        setError('删除失败，请稍后重试');
      }
    }
    setShowDeleteFunnelConfirm(false);
    setDeletingFunnelId(null);
  };

  // 取消删除漏斗
  const cancelDeleteFunnel = () => {
    setShowDeleteFunnelConfirm(false);
    setDeletingFunnelId(null);
  };

  // 打开编辑漏斗弹窗
  const handleEditFunnel = (funnelId) => {
    const funnel = funnels.find(f => f.id === funnelId);
    if (funnel) {
      setEditingFunnelId(funnelId);
      setFunnelName(funnel.name);
      setFunnelIncludeKeywords(funnel.include_keywords || '');
      setFunnelExcludeKeywords(funnel.exclude_keywords || '');
      setFunnelSelectedFeeds(funnel.feed_ids || []);
      setShowEditFunnelModal(true);
      setShowFunnelMenu(false);
    }
  };

  // 关闭编辑漏斗弹窗
  const handleCloseEditFunnelModal = () => {
    setShowEditFunnelModal(false);
    setEditingFunnelId(null);
    setFunnelName('');
    setFunnelIncludeKeywords('');
    setFunnelExcludeKeywords('');
    setFunnelSelectedFeeds([]);
    setFunnelError('');
  };

  // 更新漏斗
  const updateFunnel = async () => {
    if (!funnelName.trim()) {
      setFunnelError('请输入漏斗名称');
      return;
    }

    setAddingFunnel(true);
    setFunnelError('');

    try {
      const response = await apiClient.post('/funnel/update', {
        funnel_id: editingFunnelId,
        name: funnelName.trim(),
        include_keywords: funnelIncludeKeywords.trim(),
        exclude_keywords: funnelExcludeKeywords.trim(),
        feed_ids: funnelSelectedFeeds
      });

      if (response.code === 0) {
        // 更新成功，刷新漏斗列表
        fetchFunnels();
        handleCloseEditFunnelModal();
        // 如果当前正在查看这个漏斗，重新搜索
        if (selectedFunnelId === editingFunnelId) {
          searchByFunnel(editingFunnelId, 1, false);
        }
      } else {
        setFunnelError(response.message || '更新失败');
      }
    } catch (err) {
      console.error('更新漏斗失败:', err);
      setFunnelError('更新失败，请稍后重试');
    } finally {
      setAddingFunnel(false);
    }
  };

  // 通过漏斗搜索文档
  const searchByFunnel = async (funnelId, page = 1, isLoadMore = false) => {
    const funnel = funnels.find(f => f.id === funnelId);
    if (!funnel) return;

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setIsSearching(true);
        setLoading(true);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        include_keywords: funnel.include_keywords || '',
        exclude_keywords: funnel.exclude_keywords || ''
      });
      params.append('translate', showTranslation.toString());

      // 如果选择了特定的订阅源，则添加到参数中
      if (funnel.feed_ids && funnel.feed_ids.length > 0) {
        funnel.feed_ids.forEach(feedId => params.append('feed_ids', feedId));
      }

      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DOCUMENT_SEARCH}?${params.toString()}`);

      if (response.code === 0) {
        if (isLoadMore) {
          setFeedItems(prev => [...prev, ...(response.data || [])]);
        } else {
          setFeedItems(response.data || []);
        }
        setSearchTotal(response.pagination?.total || 0);
        setShowSearchResult(true);
        setHasMore(response.pagination?.has_more ?? false);
        setSearchPage(page);
        setError(null);
      } else {
        if (!isLoadMore) {
          setError(response.message || '搜索失败');
          setShowSearchResult(false);
        }
      }
    } catch (err) {
      console.error('漏斗搜索失败:', err);
      if (!isLoadMore) {
        setError('搜索失败，请稍后重试');
        setShowSearchResult(false);
      }
    } finally {
      setIsSearching(false);
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 处理漏斗选择
  const handleFunnelSelect = (e, funnelId) => {
    e.stopPropagation();

    // 清除之前的搜索状态
    setShowSearchResult(false);
    setSearchKeyword('');
    setSearchTotal(0);
    setSearchPage(1);
    setError(null);
    setShowingFavorites(false);
    setSelectedFeedId(null);

    if (selectedFunnelId === funnelId) {
      // 取消选择
      setSelectedFunnelId(null);
      setShowingFunnel(false);
      navigate('/');
      fetchFeedItems(1, false, null);
    } else {
      // 选择漏斗，导航到 /funnel/:id
      setSelectedFunnelId(funnelId);
      setShowingFunnel(true);
      navigate(`/funnel/${funnelId}`);
      searchByFunnel(funnelId, 1, false);
    }
  };

  // 打开添加漏斗弹窗
  const handleOpenAddFunnelModal = () => {
    setShowAddFunnelModal(true);
    setFunnelName('');
    setFunnelIncludeKeywords('');
    setFunnelExcludeKeywords('');
    setFunnelSelectedFeeds([]);
    setFunnelError('');
  };

  // 关闭添加漏斗弹窗
  const handleCloseAddFunnelModal = () => {
    setShowAddFunnelModal(false);
    setFunnelName('');
    setFunnelIncludeKeywords('');
    setFunnelExcludeKeywords('');
    setFunnelSelectedFeeds([]);
    setFunnelError('');
  };

  // 处理订阅源多选
  const handleFeedCheckboxChange = (feedId) => {
    if (funnelSelectedFeeds.includes(feedId)) {
      setFunnelSelectedFeeds(prev => prev.filter(id => id !== feedId));
    } else {
      setFunnelSelectedFeeds(prev => [...prev, feedId]);
    }
  };

  // 从 API 获取 Feed 列表
  const fetchFeeds = async () => {
    try {
      setFeedsLoading(true);
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.FEED_LIST);
      if (response.code === 0) {
        setFeeds(response.data || []);
      } else {
        console.error('获取 Feed 列表失败:', response.message);
      }
    } catch (err) {
      console.error('获取 Feed 列表失败:', err);
    } finally {
      setFeedsLoading(false);
    }
  };

  // 从 API 获取文档列表
  const fetchFeedItems = async (page = 1, isLoadMore = false, feedId = null) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      // 构建 URL 参数
      const params = new URLSearchParams({ page: page.toString() });
      if (feedId) {
        params.append('feed_id', feedId.toString());
      }
      // 添加翻译参数
      params.append('translate', showTranslation.toString());
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DOCUMENT_LIST}?${params.toString()}`);
      // 后端返回的数据结构: { code: 0, message: 'success', data: [...], pagination: {...} }
      if (response.code === 0) {
        if (isLoadMore) {
          // 追加加载：将新数据追加到现有列表
          setFeedItems(prev => [...prev, ...(response.data || [])]);
        } else {
          // 首次加载：直接设置数据
          setFeedItems(response.data || []);
        }
        setHasMore(response.pagination?.has_more ?? true);
      } else {
        setError(response.message || '加载数据失败');
      }
    } catch (err) {
      console.error('获取文档列表失败:', err);
      setError('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 获取用户收藏列表
  const fetchFavorites = async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = new URLSearchParams({ page: page.toString() });
      params.append('translate', showTranslation.toString());
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.USER_FAVORITES}?${params.toString()}`);
      if (response.code === 0) {
        if (isLoadMore) {
          // 追加加载：将新数据追加到现有列表
          setFeedItems(prev => [...prev, ...(response.data || [])]);
        } else {
          // 首次加载：直接设置数据
          setFeedItems(response.data || []);
        }
        setHasMore(response.pagination?.has_more ?? false);
      } else {
        setError(response.message || '加载数据失败');
      }
    } catch (err) {
      console.error('获取收藏列表失败:', err);
      setError('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 搜索文档
  const searchDocuments = async (keyword, page = 1, isLoadMore = false, feedId = null) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setIsSearching(true);
        setLoading(true);
      }
      const params = new URLSearchParams({ keyword: keyword, page: page.toString() });
      params.append('translate', showTranslation.toString());
      // 如果有 feed_id，则添加到参数中
      if (feedId) {
        params.append('feed_id', feedId.toString());
      }
      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DOCUMENT_SEARCH}?${params.toString()}`);

      if (response.code === 0) {
        if (isLoadMore) {
          // 追加加载：将新数据追加到现有列表
          setFeedItems(prev => [...prev, ...(response.data || [])]);
        } else {
          // 首次加载：直接设置数据
          setFeedItems(response.data || []);
        }
        setSearchTotal(response.pagination?.total || 0);
        setShowSearchResult(true);
        setHasMore(response.pagination?.has_more ?? false);
        setSearchPage(page);
        // 搜索完成后隐藏移动端搜索弹窗
        if (!isLoadMore && window.innerWidth < 1024) {
          setShowSearchInput(false);
        }
        if (response.data.length === 0 && !isLoadMore) {
          setError('未找到匹配的文档');
        } else {
          setError(null);
        }
      } else {
        if (!isLoadMore) {
          setError(response.message || '搜索失败');
          setShowSearchResult(false);
        }
      }
    } catch (err) {
      console.error('搜索失败:', err);
      if (!isLoadMore) {
        setError('搜索失败，请稍后重试');
        setShowSearchResult(false);
      }
    } finally {
      setIsSearching(false);
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 处理搜索输入
  const handleSearch = (keyword) => {
    if (!keyword.trim()) {
      // 清空搜索，恢复列表
      setShowSearchResult(false);
      setSearchTotal(0);
      setSearchPage(1);
      setCurrentPage(1);
      setShowingFunnel(false);
      setSelectedFunnelId(null);
      fetchFeedItems(1, false, selectedFeedId);
      return;
    }
    setSearchPage(1);
    setShowingFunnel(false);
    setSelectedFunnelId(null);
    searchDocuments(keyword.trim(), 1, false, selectedFeedId);
  };

  // 处理搜索输入框回车
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch(searchKeyword);
    }
  };

  // 处理点击搜索按钮
  const handleSearchClick = () => {
    // 移动端点击显示输入框
    if (window.innerWidth < 1024) {
      setShowSearchInput(!showSearchInput);
    } else {
      // 桌面端直接执行搜索
      handleSearch(searchKeyword);
    }
  };

  // 清除搜索
  const clearSearch = () => {
    setSearchKeyword('');
    setShowSearchInput(false);
    setShowSearchResult(false);
    setSearchTotal(0);
    setSearchPage(1);
    setError(null);
    setCurrentPage(1);
    if (showingFavorites) {
      fetchFavorites(1, false);
    } else {
      fetchFeedItems(1, false, selectedFeedId);
    }
  };

  // 搜索订阅源
  const searchFeeds = async (keyword, page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setExploreLoading(true);
      }

      const params = new URLSearchParams({
        keyword: keyword,
        page: page.toString()
      });

      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.FEED_SEARCH}?${params.toString()}`);

      if (response.code === 0) {
        if (isLoadMore) {
          setExploreResults(prev => [...prev, ...(response.data || [])]);
        } else {
          setExploreResults(response.data || []);
        }
        setExploreTotal(response.pagination?.total || 0);
        setExploreHasMore(response.pagination?.has_more ?? false);
        setExplorePage(page);

        if (response.data.length === 0 && !isLoadMore) {
          setError('未找到匹配的订阅源');
        } else {
          setError(null);
        }
      } else {
        if (!isLoadMore) {
          setError(response.message || '搜索失败');
        }
      }
    } catch (err) {
      console.error('搜索订阅源失败:', err);
      if (!isLoadMore) {
        setError('搜索失败，请稍后重试');
      }
    } finally {
      setExploreLoading(false);
      setLoadingMore(false);
    }
  };

  // 处理探索搜索
  const handleExploreSearch = (keyword) => {
    if (!keyword.trim()) {
      setExploreResults([]);
      setExploreTotal(0);
      // 重新加载热门文档
      fetchHotDocuments(1, false);
      return;
    }
    setExplorePage(1);
    searchFeeds(keyword.trim(), 1, false);
  };

  // 处理探索搜索输入框回车
  const handleExploreSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleExploreSearch(exploreKeyword);
    }
  };

  // 探索页面加载更多
  const handleExploreLoadMore = () => {
    if (!loadingMore && exploreHasMore && exploreKeyword) {
      const nextPage = explorePage + 1;
      setExplorePage(nextPage);
      searchFeeds(exploreKeyword, nextPage, true);
    }
  };

  // 处理探索页面订阅feed
  const handleExploreSubscribe = async (feedId) => {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.FEED_ADD, {
        feed_id: feedId
      });

      if (response.code === 0) {
        // 订阅成功，更新状态
        setExploreResults(prev =>
          prev.map(feed =>
            feed.id === feedId ? { ...feed, is_subscribed: true } : feed
          )
        );
        // 刷新侧边栏feed列表
        fetchFeeds();
      } else {
        setError(response.message || '订阅失败');
      }
    } catch (err) {
      console.error('订阅失败:', err);
      setError('订阅失败，请稍后重试');
    }
  };

  // 处理探索页面取消订阅
  const handleExploreUnsubscribe = async (feedId) => {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.FEED_DELETE, {
        feed_id: feedId
      });

      if (response.code === 0) {
        // 取消订阅成功，更新本地状态
        setExploreResults(prev =>
          prev.map(feed =>
            feed.id === feedId ? { ...feed, is_subscribed: false } : feed
          )
        );
        // 刷新侧边栏feed列表
        fetchFeeds();
      } else {
        setError(response.message || '取消订阅失败');
      }
    } catch (err) {
      console.error('取消订阅失败:', err);
      setError('取消订阅失败，请稍后重试');
    }
  };

  // 获取热门文档
  const fetchHotDocuments = async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setHotLoading(true);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '10'
      });
      params.append('translate', showTranslation.toString());

      const response = await apiClient.get(`${API_CONFIG.ENDPOINTS.DOCUMENT_HOT}?${params.toString()}`);

      if (response.code === 0) {
        if (isLoadMore) {
          setHotDocuments(prev => [...prev, ...(response.data || [])]);
        } else {
          setHotDocuments(response.data || []);
        }
        setHotHasMore(response.pagination?.has_more ?? false);
        setHotTotal(response.pagination?.total || 0);
      } else {
        if (!isLoadMore) {
          setError(response.message || '获取热门文档失败');
        }
      }
    } catch (err) {
      console.error('获取热门文档失败:', err);
      if (!isLoadMore) {
        setError('获取热门文档失败，请稍后重试');
      }
    } finally {
      setHotLoading(false);
      setLoadingMore(false);
    }
  };

  // 热门文档加载更多
  const handleHotLoadMore = () => {
    if (!loadingMore && hotHasMore) {
      const nextPage = hotPage + 1;
      setHotPage(nextPage);
      fetchHotDocuments(nextPage, true);
    }
  };

  // 点击feed名称跳转到feed详情页
  const handleFeedClick = (feedId) => {
    navigate(`/feed/${feedId}`);
  };

  useEffect(() => {
    fetchFeeds();
    fetchFunnels();
  }, []);

  // 解析URL路径获取参数
  const parsePathParams = (pathname) => {
    // 匹配 /doc/:id 格式（ID 可以是任何字符串，包括 UUID）
    const docMatch = pathname.match(/^\/doc\/([^/]+)$/);
    if (docMatch) {
      return { type: 'doc', id: docMatch[1] };
    }
    // 匹配 /feed/:id 格式（ID 可以是任何字符串，包括 UUID）
    const feedMatch = pathname.match(/^\/feed\/([^/]+)$/);
    if (feedMatch) {
      return { type: 'feed', id: feedMatch[1] };
    }
    // 匹配 /funnel/:id 格式（ID 可以是任何字符串，包括 UUID）
    const funnelMatch = pathname.match(/^\/funnel\/([^/]+)$/);
    if (funnelMatch) {
      return { type: 'funnel', id: funnelMatch[1] };
    }
    // 匹配 /favorites 格式
    if (pathname === '/favorites') {
      return { type: 'favorites' };
    }
    // 匹配 /explore 格式
    if (pathname === '/explore') {
      return { type: 'explore' };
    }
    // 默认首页
    return { type: 'home' };
  };

  // 初始化时从 URL 读取参数，并监听 URL 变化
  useEffect(() => {
    // 未登录用户自动跳转到探索页面
    const token = localStorage.getItem('token');
    if (!token && location.pathname !== '/explore' && location.pathname !== '/auth' && location.pathname !== '/profile') {
      navigate('/explore');
      return;
    }

    const pathParams = parsePathParams(location.pathname);

    if (pathParams.type === 'doc') {
      setSelectedDocumentId(pathParams.id);
    } else {
      setSelectedDocumentId(null);
    }

    if (pathParams.type === 'favorites') {
      // 收藏视图
      setShowingFavorites(true);
      setSelectedFeedId(null);
      fetchFavorites(1, false);
    } else if (pathParams.type === 'feed') {
      // Feed 视图
      setShowingFavorites(false);
      setSelectedFeedId(pathParams.id);
      fetchFeedItems(1, false, pathParams.id);
    } else if (pathParams.type === 'funnel') {
      // 漏斗视图
      setShowingFavorites(false);
      setSelectedFeedId(null);
      setShowingFunnel(true);
      setSelectedFunnelId(pathParams.id);
      searchByFunnel(pathParams.id, 1, false);
    } else if (pathParams.type === 'explore') {
      // 探索页面
      setShowingFavorites(false);
      setSelectedFeedId(null);
      // 获取热门文档
      fetchHotDocuments(1, false);
    } else {
      // 默认视图
      setShowingFavorites(false);
      setSelectedFeedId(null);
      setShowingFunnel(false);
      setSelectedFunnelId(null);
      fetchFeedItems(1, false, null);
    }
  }, [location.pathname]);

  // 处理滚动事件，实现自动翻页
  const handleScroll = (e) => {
    if (!autoScrollLoad || loadingMore) return;

    // 判断当前是否在探索页面
    const isExplorePage = location.pathname === '/explore';

    // 根据当前页面判断是否还有更多数据
    let currentHasMore = false;
    if (isExplorePage) {
      // 探索页面：如果有搜索结果则使用 exploreHasMore，否则使用 hotHasMore
      currentHasMore = (exploreResults.length > 0 && exploreHasMore) || (exploreResults.length === 0 && hotHasMore);
    } else {
      // 其他页面使用 hasMore
      currentHasMore = hasMore;
    }

    if (!currentHasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // 当滚动到底部附近时触发加载
    const threshold = 100; // 距离底部100px时触发
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      handleLoadMore();
    }
  };

  // 加载更多
  const handleLoadMore = () => {
    if (loadingMore) return;

    // 判断当前是否在探索页面
    const isExplorePage = location.pathname === '/explore';

    if (isExplorePage) {
      // 探索页面加载更多
      if (exploreResults.length > 0 && exploreHasMore) {
        // 搜索结果加载更多
        const nextPage = explorePage + 1;
        setExplorePage(nextPage);
        searchFeeds(exploreKeyword, nextPage, true);
      } else if (exploreResults.length === 0 && hotHasMore) {
        // 热门文档加载更多
        const nextPage = hotPage + 1;
        setHotPage(nextPage);
        fetchHotDocuments(nextPage, true);
      }
      return;
    }

    // 其他页面加载更多
    if (!hasMore) return;

    if (showingFunnel && selectedFunnelId) {
      // 漏斗状态下加载更多
      const nextPage = searchPage + 1;
      setSearchPage(nextPage);
      searchByFunnel(selectedFunnelId, nextPage, true);
    } else if (showSearchResult && searchKeyword) {
      // 搜索状态下加载更多
      const nextPage = searchPage + 1;
      setSearchPage(nextPage);
      searchDocuments(searchKeyword, nextPage, true, selectedFeedId);
    } else if (showingFavorites) {
      // 收藏列表状态下加载更多
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchFavorites(nextPage, true);
    } else {
      // 普通列表状态下加载更多
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchFeedItems(nextPage, true, selectedFeedId);
    }
  };

  // 处理收藏视图切换
  const handleShowFavorites = (e) => {
    e?.stopPropagation();

    // 未登录用户跳转登录页，登录后返回收藏页
    if (!currentUser) {
      navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent('/favorites')}` });
      return;
    }

    // 清除搜索状态
    setShowSearchResult(false);
    setSearchKeyword('');
    setSearchTotal(0);
    setSearchPage(1);
    setError(null);

    // 清除漏斗状态
    setShowingFunnel(false);
    setSelectedFunnelId(null);

    // 切换收藏视图
    const newState = !showingFavorites;
    setShowingFavorites(newState);
    setSelectedFeedId(null);
    setCurrentPage(1);

    // 更新URL
    if (newState) {
      navigate('/favorites');
    } else {
      navigate('/');
    }

    // 加载收藏列表或普通列表
    if (newState) {
      fetchFavorites(1, false);
    } else {
      fetchFeedItems(1, false, null);
    }
  };

  // 处理 Feed 选择
  const handleFeedSelect = (e, feedId) => {
    e.stopPropagation();

    // 清除搜索状态
    setShowSearchResult(false);
    setSearchKeyword('');
    setSearchTotal(0);
    setSearchPage(1);
    setError(null);
    setShowingFavorites(false); // 关闭收藏视图
    setShowingFunnel(false); // 关闭漏斗视图
    setSelectedFunnelId(null); // 清除漏斗选择

    // 直接判断：点击当前已选中的则取消选择，否则选择新的
    if (selectedFeedId === feedId) {
      setSelectedFeedId(null);
      setCurrentPage(1);
      navigate('/');
    } else {
      setSelectedFeedId(feedId);
      setCurrentPage(1);
      // 只有当 feedId 不为 null 时才跳转到 feed 路径
      if (feedId) {
        navigate(`/feed/${feedId.toString()}`);
      } else {
        navigate('/');
      }
    }
  };

  // 打开添加 Feed 弹窗
  const handleOpenAddFeedModal = () => {
    setShowAddFeedModal(true);
    setFeedLink('');
    setFeedError('');
  };

  // 关闭添加 Feed 弹窗
  const handleCloseAddFeedModal = () => {
    setShowAddFeedModal(false);
    setFeedLink('');
    setFeedError('');
  };

  // 添加 Feed
  const handleAddFeed = async () => {
    // 验证输入
    if (!feedLink.trim()) {
      setFeedError('请输入 RSS 链接');
      return;
    }

    if (!feedLink.startsWith('http://') && !feedLink.startsWith('https://')) {
      setFeedError('链接格式不正确，必须以 http:// 或 https:// 开头');
      return;
    }

    setAddingFeed(true);
    setFeedError('');

    try {
      // 使用 JSON 格式发送 POST 请求
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.FEED_ADD, {
        link: feedLink.trim(),
      });

      if (response.code === 0) {
        // 添加成功，运行爬虫
        const feedId = response.data?.id;
        try {
          await apiClient.post(API_CONFIG.ENDPOINTS.RUN_SPIDER, {
            feed_id: feedId,
          });
        } catch (spiderErr) {
          console.error('启动爬虫失败:', spiderErr);
          // 爬虫启动失败不影响添加成功的结果
        }

        // 刷新页面
        window.location.reload();
      } else {
        setFeedError(response.message || '添加失败');
      }
    } catch (err) {
      console.error('添加 Feed 失败:', err);
      setFeedError('添加失败，请稍后重试');
    } finally {
      setAddingFeed(false);
    }
  };

  // 处理复制链接
  const handleCopyLink = () => {
    const feed = feeds.find(f => f.id === selectedFeedId);
    if (feed) {
      const textToCopy = feed.link || window.location.href;

      // 尝试使用现代剪贴板 API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy)
          .then(() => {
            console.log('链接已复制');
            setShowFeedMenu(false);
          })
          .catch(err => {
            console.error('复制失败:', err);
            // 如果现代 API 失败，尝试使用传统方法
            copyWithFallback(textToCopy);
          });
      } else {
        // 使用传统的 document.execCommand 方法
        copyWithFallback(textToCopy);
      }
    }
  };

  // 传统复制方法的回退方案
  const copyWithFallback = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // 防止在页面上显示 textarea
      textArea.style.position = 'fixed';
      textArea.style.top = '-9999px';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      if (successful) {
        console.log('链接已复制');
      } else {
        console.error('复制失败');
      }
      document.body.removeChild(textArea);
      setShowFeedMenu(false);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 显示取消订阅确认框
  const handleUnsubscribe = () => {
    setShowUnsubscribeConfirm(true);
    setShowFeedMenu(false);
  };

  // 确认取消订阅
  const confirmUnsubscribe = async () => {
    if (selectedFeedId) {
      try {
        await apiClient.post(API_CONFIG.ENDPOINTS.FEED_DELETE, {
          feed_id: selectedFeedId
        });
        // 删除成功后刷新页面
        window.location.reload();
      } catch (err) {
        console.error('取消订阅失败:', err);
      }
    }
    setShowUnsubscribeConfirm(false);
  };

  // 取消取消订阅
  const cancelUnsubscribe = () => {
    setShowUnsubscribeConfirm(false);
  };

  // 处理弹窗内的回车键
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !addingFeed) {
      handleAddFeed();
    }
  };

  // 处理点击文档详情
  const handleDocumentClick = async (e, documentId, rid) => {
    // 如果点击的是标题或右箭头，进入详情页
    if (e.target.closest('.document-title') || e.target.closest('.chevron-icon')) {
      e.preventDefault();

      // 标记文档为已读
      if (rid && currentUser) {
        try {
          await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_MARK_READ, {
            rid: rid
          });
          // 更新本地状态
          setFeedItems(prev => prev.map(item =>
            item.id === documentId ? { ...item, is_read: true } : item
          ));
        } catch (err) {
          console.error('标记已读失败:', err);
        }
      }

      setSelectedDocumentId(documentId);

      // 更新 URL，跳转到文档详情路径
      navigate(`/doc/${documentId}`);
    }
  };

  // 切换文档已读状态
  const handleToggleReadStatus = async (e, item) => {
    e.stopPropagation();
    const newStatus = !item.is_read;

    if (!item.rid || !currentUser) {
      return;
    }

    try {
      if (newStatus) {
        await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_MARK_READ, {
          rid: item.rid
        });
      } else {
        await apiClient.post(API_CONFIG.ENDPOINTS.DOCUMENT_MARK_UNREAD, {
          rid: item.rid
        });
      }
      // 更新本地状态
      setFeedItems(prev => prev.map(doc =>
        doc.id === item.id ? { ...doc, is_read: newStatus } : doc
      ));
    } catch (err) {
      console.error('切换已读状态失败:', err);
    }
  };

  // 如果是 auth 路由，显示登录注册页面
  if (location.pathname === '/auth') {
    return <Auth />;
  }

  // 如果是 profile 路由，显示个人主页
  if (location.pathname === '/profile') {
    return <Profile />;
  }

  // 如果选择了文档，显示详情页
  if (selectedDocumentId) {
    return <DocumentDetail />;
  }

  return (
    <div className="flex h-screen bg-[#f8f9fa] text-[#333] font-sans overflow-hidden">
      {/* 移动端侧边栏遮罩 */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 左侧边栏 */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transition-transform duration-300 transform 
        lg:relative lg:translate-x-0 
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo 区 */}
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center">
              <span className="text-white font-bold italic">f</span>
            </div>
            <h1 className="text-xl font-bold tracking-tight">漏斗Feed</h1>
          </div>

          {/* 频道列表 */}
          <nav className="flex-1 overflow-y-auto p-3 hide-scrollbar">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3 flex items-center justify-between">
              <span>订阅源</span>
              <button
                onClick={handleOpenAddFunnelModal}
                className="text-gray-400 hover:text-green-600 transition-colors p-1"
                title="添加漏斗"
              >
                <FunnelPlus className="w-4 h-4" />
              </button>
            </div>
            {/* 全部文章 */}
            <button
              onClick={(e) => handleFeedSelect(e, null)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors mb-2 ${
                selectedFeedId === null && !showingFunnel
                ? 'bg-green-50 text-green-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Rss className="w-4 h-4" />
              <span className="truncate">全部文章</span>
              {(selectedFeedId === null && !showingFunnel) && <div className="ml-auto w-1.5 h-1.5 bg-green-500 rounded-full" />}
            </button>
            {/* Feed 列表 */}
            <div className="space-y-1">
              {feedsLoading ? (
                <div className="text-center py-4 text-sm text-gray-500">加载中...</div>
              ) : feeds.length === 0 ? (
                <div className="text-center py-4 text-sm text-gray-500">暂无订阅源</div>
              ) : (
                feeds.map((feed) => (
                  <button
                    key={feed.id}
                    onClick={(e) => handleFeedSelect(e, feed.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedFeedId === feed.id && !showingFunnel
                      ? 'bg-green-50 text-green-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FeedLogo src={feed.logo_url} title={feed.title} size={16} radius={4} />
                    <span className="truncate flex-1 text-left">{feed.title}</span>
                    {feed.unread_count > 0 && (
                      <span className="ml-auto text-gray-400 text-xs font-medium">
                        {feed.unread_count}
                      </span>
                    )}
                    {selectedFeedId === feed.id && !showingFunnel && <div className="ml-auto w-1.5 h-1.5 bg-green-500 rounded-full" />}
                  </button>
                ))
              )}
            </div>

            {/* 漏斗列表 */}
            {funnels.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
                  漏斗
                </div>
                <div className="space-y-1">
                  {funnelsLoading ? (
                    <div className="text-center py-4 text-sm text-gray-500">加载中...</div>
                  ) : (
                    funnels.map((funnel) => (
                      <button
                        key={funnel.id}
                        onClick={(e) => handleFunnelSelect(e, funnel.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedFunnelId === funnel.id
                          ? 'bg-green-50 text-green-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Filter className="w-4 h-4" />
                        <span className="truncate flex-1 text-left">{funnel.name}</span>
                        {selectedFunnelId === funnel.id && <div className="ml-auto w-1.5 h-1.5 bg-green-500 rounded-full" />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </nav>

          {/* 我的收藏 */}
          <div className="border-t border-gray-100 mx-4 my-1"></div>
          <nav className="px-4 py-1">
            <button
              onClick={handleShowFavorites}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                showingFavorites
                  ? 'bg-green-50 text-green-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Bookmark className="w-4 h-4" />
              <span className="truncate">收藏</span>
              {showingFavorites && <div className="ml-auto w-1.5 h-1.5 bg-green-500 rounded-full" />}
            </button>
          </nav>

          {/* 探索 */}
          <div className="border-t border-gray-100 mx-4 my-1"></div>
          <nav className="px-4 py-1">
            <button
              onClick={() => navigate('/explore')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                location.pathname === '/explore'
                  ? 'bg-green-50 text-green-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Compass className="w-4 h-4" />
              <span className="truncate">探索</span>
              {location.pathname === '/explore' && <div className="ml-auto w-1.5 h-1.5 bg-green-500 rounded-full" />}
            </button>
          </nav>

          {/* 底部设置 */}
          <div className="px-4 py-2 border-t border-gray-100 space-y-2">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 w-full hover:bg-gray-100 rounded-md"
            >
              <Settings className="w-4 h-4" />
              设置
            </button>
          </div>
        </div>
      </aside>

      {/* 主界面内容 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 顶部导航 */}
        <header className="h-16 bg-[#1a1c1e] text-white flex items-center justify-between px-4 lg:px-8 shrink-0">
          <div className="flex items-center gap-4 flex-1">
            <button 
              className="lg:hidden p-1 hover:bg-white/10 rounded"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* 添加按钮 - 桌面端显示文字+图标，移动端只显示图标 */}
            <button
              onClick={handleOpenAddFeedModal}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="sm:inline">添加</span>
            </button>
            {currentUser ? (
              // 已登录用户 - 显示铃铛和头像
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowNotificationPopup(!showNotificationPopup)}
                  className="p-2 hover:bg-white/10 rounded-full relative group"
                >
                  <Bell className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 hover:bg-white/10 p-2 rounded-[6px] transition-colors"
                  >
                    <Avatar src={currentUser.avatar} name={currentUser.nickname || currentUser.username} />
                  </button>
                  {/* 用户下拉菜单 */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 text-center">
                          {currentUser.nickname || currentUser.username}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/profile');
                        }}
                        className="w-full px-4 py-2 text-center text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                      >
                        <UserCircle className="w-4 h-4" />
                        我的主页
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-center text-sm text-red-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        退出登录
                      </button>
                    </div>
                  )}
                  {/* 通知弹窗 */}
                  {showNotificationPopup && (
                    <div className="absolute right-16 top-full mt-2 w-72 bg-white rounded-lg shadow-lg py-6 z-50">
                      <div className="flex flex-col items-center justify-center gap-3 text-gray-500">
                        <Bell className="w-12 h-12 text-gray-300" />
                        <p className="text-sm">暂无通知</p>
                      </div>
                    </div>
                  )}
                  {/* 点击外部关闭菜单 */}
                  {showUserMenu && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                  )}
                  {/* 点击外部关闭通知弹窗 */}
                  {showNotificationPopup && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowNotificationPopup(false)}
                    />
                  )}
                </div>
              </div>
            ) : (
              // 未登录用户 - 只显示登录按钮，不显示铃铛
              <button
                onClick={() => navigate({ pathname: 'auth' })}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">登录</span>
              </button>
            )}
          </div>
        </header>

        {/* 列表区域 */}
        <main className="flex-1 overflow-y-auto p-2 lg:p-8 hide-scrollbar" onScroll={handleScroll}>
          <div className="max-w-4xl mx-auto">
            {/* 探索页面 */}
            {location.pathname === '/explore' && (
              <>
                {/* 探索页面标题和搜索框 */}
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-4">探索订阅</h2>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={exploreKeyword}
                        onChange={(e) => setExploreKeyword(e.target.value)}
                        onKeyDown={handleExploreSearchKeyDown}
                        placeholder="搜索订阅源名称或链接..."
                        className="w-full bg-white border border-gray-300 rounded-md py-2 pl-10 pr-8 text-base focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none placeholder:text-gray-400"
                        style={{ fontSize: '16px' }}
                      />
                      {exploreKeyword && (
                        <button
                          onClick={() => {
                            setExploreKeyword('');
                            setExploreResults([]);
                            setExploreTotal(0);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleExploreSearch(exploreKeyword)}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      搜索
                    </button>
                  </div>
                  {exploreTotal > 0 && (
                    <div className="text-sm text-gray-500 mt-2">
                      找到 {exploreTotal} 个订阅源
                    </div>
                  )}
                </div>

                {/* 加载状态 */}
                {exploreLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">搜索中...</div>
                  </div>
                )}

                {/* 搜索结果列表 */}
                {!exploreLoading && exploreResults.length > 0 && (
                  <div className="space-y-2">
                    {exploreResults.map((feed) => (
                      <div
                        key={feed.id}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-green-200 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          {/* Feed Logo */}
                          <div className="flex-shrink-0">
                            <FeedLogo src={feed.logo_url} title={feed.title} size={48} radius={8} />
                          </div>

                          {/* Feed 信息 */}
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => handleFeedClick(feed.id)}
                              className="text-lg font-semibold text-gray-900 hover:text-green-600 transition-colors mb-1 block hover:underline truncate"
                            >
                              {feed.title}
                            </button>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                {feed.subscriber_count} 订阅
                              </span>
                            </div>
                          </div>

                          {/* 订阅/取消订阅按钮 */}
                          <div>
                            {feed.is_subscribed ? (
                              <button
                                onClick={() => handleExploreUnsubscribe(feed.id)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                              >
                                已订阅
                              </button>
                            ) : (
                              <button
                                onClick={() => handleExploreSubscribe(feed.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                订阅
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 空状态 */}
                {!exploreLoading && exploreResults.length === 0 && exploreKeyword && !error && (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">未找到匹配的订阅源</div>
                  </div>
                )}

                {/* 未搜索提示 + 热门趋势 */}
                {!exploreLoading && exploreResults.length === 0 && !exploreKeyword && !error && (
                  <div>
                    {hotLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">加载热门文档...</div>
                      </div>
                    ) : hotDocuments.length > 0 ? (
                      <>
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900">热门趋势</h3>
                        </div>
                        <div className="space-y-2">
                          {hotDocuments.map((doc) => (
                            <div
                              key={doc.id}
                              onClick={() => {
                                if (currentUser) {
                                  // 已登录用户直接查看文章详情
                                  navigate(`/doc/${doc.id}`);
                                } else {
                                  // 未登录用户跳转登录页，登录后返回文章详情
                                  navigate({ pathname: 'auth', search: `redirect=${encodeURIComponent(`/doc/${doc.id}`)}` });
                                }
                              }}
                              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-green-200 transition-all cursor-pointer"
                            >
                              <h4 className="text-base font-semibold text-gray-900 hover:text-green-600 transition-colors mb-2">
                                {doc.title}
                              </h4>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                                <span className="font-medium text-gray-700">{doc.author}</span>
                                <span>•</span>
                                <span>{doc.time}</span>
                              </div>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {stripHtmlAndTruncate(doc.description, 150)}
                              </p>
                            </div>
                          ))}
                        </div>
                        {hotHasMore && (
                          <div className="mt-8 text-center">
                            <button
                              onClick={handleHotLoadMore}
                              disabled={loadingMore}
                              className={`text-sm font-medium py-2 px-4 rounded-full border transition-all shadow-sm ${
                                'text-gray-500 hover:text-green-600 border-gray-200 hover:border-green-200 bg-white'
                              } ${loadingMore ? 'opacity-70 cursor-wait' : ''}`}
                            >
                              {loadingMore ? '加载中...' : '加载更多'}
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <Search className="w-16 h-16 mb-4 text-gray-300" />
                        <p className="text-lg font-medium mb-2">探索订阅</p>
                        <p className="text-sm">输入关键词搜索您感兴趣的 RSS 订阅源</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 加载更多 */}
                {exploreResults.length > 0 && exploreHasMore && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={handleExploreLoadMore}
                      disabled={loadingMore}
                      className={`text-sm font-medium py-2 px-4 rounded-full border transition-all shadow-sm ${
                        'text-gray-500 hover:text-green-600 border-gray-200 hover:border-green-200 bg-white'
                      } ${loadingMore ? 'opacity-70 cursor-wait' : ''}`}
                    >
                      {loadingMore ? '加载中...' : '加载更多'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* 原有的文档列表内容 */}
            {location.pathname !== '/explore' && (
              <>
                {/* 当前视图标题 */}
                <div className="flex items-center justify-between mb-6">
              <div className="relative">
                <h2
                  id="feed-title"
                  className="text-xl font-bold flex items-center gap-2 cursor-pointer"
                  onClick={() => {
                    if (showingFunnel && selectedFunnelId) {
                      setShowFunnelMenu(!showFunnelMenu);
                    } else if (selectedFeedId && !showingFavorites) {
                      setShowFeedMenu(!showFeedMenu);
                    }
                  }}
                >
                  {showingFavorites
                    ? <span>我的收藏</span>
                    : showingFunnel
                    ? <span className="flex items-center gap-2">
                        <Filter className="w-5 h-5" />
                        {funnels.find(f => f.id === selectedFunnelId)?.name || '漏斗'}
                      </span>
                    : selectedFeedId
                    ? <span>{feeds.find(f => f.id === selectedFeedId)?.title || '未知来源'}</span>
                    : <span>全部文章</span>
                  }
                  {selectedFeedId && !showingFavorites && !showingFunnel && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        showFeedMenu ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                  {showingFunnel && selectedFunnelId && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        showFunnelMenu ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </h2>

                {/* 搜索结果提示 */}
                {showSearchResult && (
                  <div className="text-sm text-green-600 mt-1">
                    搜索到 {searchTotal} 个文档
                    <button
                      onClick={clearSearch}
                      className="ml-2 text-xs text-gray-500 hover:text-red-600"
                    >
                      清除搜索
                    </button>
                  </div>
                )}

                {/* Feed 操作菜单 Popup */}
                {showFeedMenu && selectedFeedId && (
                  <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <button
                      onClick={handleCopyLink}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      复制链接
                    </button>
                    <button
                      onClick={handleUnsubscribe}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      取消订阅
                    </button>
                  </div>
                )}

                {/* 漏斗操作菜单 Popup */}
                {showFunnelMenu && selectedFunnelId && (
                  <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <button
                      onClick={() => handleEditFunnel(selectedFunnelId)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      编辑漏斗
                    </button>
                    <button
                      onClick={() => handleDeleteFunnel(selectedFunnelId)}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      删除漏斗
                    </button>
                  </div>
                )}

                {/* 点击外部关闭菜单 */}
                {showFeedMenu && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFeedMenu(false)}
                  />
                )}
              </div>
              <div className="flex gap-2 text-gray-400 relative items-center">
                {/* 搜索按钮 - 只在移动端显示 */}
                <button
                  onClick={handleSearchClick}
                  className={`lg:hidden p-1 hover:bg-gray-100 rounded transition transition-colors ${
                    showSearchResult ? 'text-green-600' : ''
                  }`}
                >
                  <Search className="w-5 h-5" />
                </button>

                {/* 搜索输入框 - 桌面端显示，移动端点击后显示 */}
                <div className={`flex items-center transition-all ${showSearchInput || window.innerWidth >= 1024 ? 'flex' : 'hidden'}`}>
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="搜索文档..."
                    className="bg-gray-100 border-none rounded-md py-1 px-3.5 text-base focus:ring-2 focus:ring-green-500 outline-none placeholder:text-gray-400 w-48"
                    style={{ fontSize: '16px' }}
                  />
                  {searchKeyword && (
                    <button
                      onClick={clearSearch}
                      className="ml-1 p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>

                <LayoutGrid
                  id="layout-grid-button"
                  className={`w-5 h-5 cursor-pointer transition-colors ${
                    isGridView ? 'text-green-600' : 'hover:text-gray-600'
                  }`}
                  onClick={() => {
                    const newValue = !isGridView;
                    setIsGridView(newValue);
                    localStorage.setItem('isGridView', newValue.toString());
                  }}
                />
                <MoreVertical
                  id="more-menu-button"
                  className={`w-5 h-5 cursor-pointer transition-colors ${
                    showMoreMenu ? 'text-green-600' : 'hover:text-gray-600'
                  }`}
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                />
                {/* 更多菜单 Popup */}
                {showMoreMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <button
                      onClick={() => {
                        setCurrentPage(1);
                        fetchFeedItems(1, false, selectedFeedId);
                        setShowMoreMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <RotateCw className="w-4 h-4" />
                      刷新列表
                    </button>
                    <div className="border-t border-gray-100 my-2" />
                    <button
                      onClick={() => setShowMoreMenu(false)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <HelpCircle className="w-4 h-4" />
                      帮助
                    </button>
                  </div>
                )}
                {/* 点击外部关闭更多菜单 */}
                {showMoreMenu && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMoreMenu(false)}
                  />
                )}
              </div>
            </div>

            {/* 加载状态 */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">加载中...</div>
              </div>
            )}

            {/* 错误状态 */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{error}</div>
              </div>
            )}

            {/* 列表项 */}
            {!loading && !error && (
              <div className={isGridView ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
                {feedItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={(e) => handleDocumentClick(e, item.id, item.rid)}
                    className="group bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md hover:border-green-200 transition-all cursor-pointer relative"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <button
                          className={`text-md font-semibold mb-1 block hover:underline break-all text-left document-title transition-colors ${
                            item.is_read
                              ? 'text-gray-400 group-hover:text-green-500'
                              : 'text-gray-900 group-hover:text-green-600'
                          }`}
                        >
                          {item.title}
                        </button>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                          <span className="font-medium text-gray-700">{item.author}</span>
                          <span>•</span>
                          <span>{item.time}</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed break-all">
                          {stripHtmlAndTruncate(item.description, 150)}
                        </p>
                      </div>

                      {!showSearchResult && (
                      <div className="flex items-center flex-shrink-0 w-5 justify-center">
                        <button
                          onClick={(e) => handleToggleReadStatus(e, item)}
                          className={`p-1 rounded transition-colors hover:bg-gray-100 ${
                            item.is_read ? 'text-gray-300' : 'text-gray-300'
                          }`}
                          title={item.is_read ? '标记为未读' : '标记为已读'}
                        >
                          {item.is_read ? (
                            <CircleCheck className="w-4 h-4" />
                          ) : (
                            <Circle className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 加载更多 */}
            <div className="mt-8 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore || !hasMore}
                className={`text-sm font-medium py-2 px-4 rounded-full border transition-all shadow-sm ${
                  !hasMore
                    ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                    : 'text-gray-500 hover:text-green-600 border-gray-200 hover:border-green-200 bg-white'
                } ${loadingMore ? 'opacity-70 cursor-wait' : ''}`}
              >
                {loadingMore ? '加载中...' : '查看更多'}
              </button>
            </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Add Feed 弹窗 */}
      {showAddFeedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseAddFeedModal}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {/* 关闭按钮 */}
            <button
              onClick={handleCloseAddFeedModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              添加 RSS 订阅源
            </h3>

            {/* 输入框 */}
            <div className="mb-4">
              <label
                htmlFor="feed-link"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                RSS 链接
              </label>
              <input
                id="feed-link"
                type="url"
                value={feedLink}
                onChange={(e) => {
                  setFeedLink(e.target.value);
                  setFeedError('');
                }}
                onKeyDown={handleKeyDown}
                placeholder="https://example.com/rss"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFeed}
              />
            </div>

            {/* 错误提示 */}
            {feedError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{feedError}</p>
              </div>
            )}

            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseAddFeedModal}
                disabled={addingFeed}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                取消
              </button>
              <button
                onClick={handleAddFeed}
                disabled={addingFeed}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingFeed ? '添加中...' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 取消订阅确认弹窗 */}
      {showUnsubscribeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelUnsubscribe}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              确认取消订阅
            </h3>
            {/* 确认信息 */}
            <p className="text-sm text-gray-600 mb-6">
              您确定要取消订阅“{feeds.find(f => f.id === selectedFeedId)?.title || '该订阅源'}”吗？
              取消后将不再接收该订阅源的更新。
            </p>
            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelUnsubscribe}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={confirmUnsubscribe}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                确认取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 设置弹窗 */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSettingsModal(false)}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowSettingsModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              设置
            </h3>

            {/* 设置选项 */}
            <div className="space-y-4">
              {/* 显示翻译开关 */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">显示翻译</p>
                  <p className="text-xs text-gray-500 mt-1">英文内容显示中文翻译标题</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTranslation}
                    onChange={handleToggleTranslation}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              {/* 自动翻页开关 */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">自动翻页</p>
                  <p className="text-xs text-gray-500 mt-1">滚动到底部时自动加载下一页</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoScrollLoad}
                    onChange={handleToggleAutoScrollLoad}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 移动端搜索弹窗 */}
      {showSearchInput && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 lg:hidden">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSearchInput(false)}
          />
          {/* 搜索框内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-4 flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="搜索文档..."
                className="w-full bg-gray-100 border-none rounded-md py-2 pl-10 pr-8 text-base focus:ring-2 focus:ring-green-500 outline-none placeholder:text-gray-400"
                style={{ fontSize: '16px' }}
              />
            </div>
            <button
              onClick={() => handleSearch(searchKeyword)}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              搜索
            </button>
            {searchKeyword && (
              <button
                onClick={() => {
                  setSearchKeyword('');
                  clearSearch();
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* 添加漏斗弹窗 */}
      {showAddFunnelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseAddFunnelModal}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {/* 关闭按钮 */}
            <button
              onClick={handleCloseAddFunnelModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              添加漏斗
            </h3>

            {/* 漏斗名称 */}
            <div className="mb-4">
              <label
                htmlFor="funnel-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                漏斗名称
              </label>
              <input
                id="funnel-name"
                type="text"
                value={funnelName}
                onChange={(e) => {
                  setFunnelName(e.target.value);
                  setFunnelError('');
                }}
                placeholder="例如：技术新闻"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 包含关键词 */}
            <div className="mb-4">
              <label
                htmlFor="include-keywords"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                包含关键词（可选）
              </label>
              <input
                id="include-keywords"
                type="text"
                value={funnelIncludeKeywords}
                onChange={(e) => {
                  setFunnelIncludeKeywords(e.target.value);
                  setFunnelError('');
                }}
                placeholder="多个关键词用逗号分隔，例如：AI,人工智能"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 不包含关键词 */}
            <div className="mb-4">
              <label
                htmlFor="exclude-keywords"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                不包含关键词（可选）
              </label>
              <input
                id="exclude-keywords"
                type="text"
                value={funnelExcludeKeywords}
                onChange={(e) => {
                  setFunnelExcludeKeywords(e.target.value);
                  setFunnelError('');
                }}
                placeholder="多个关键词用逗号分隔，例如：广告,推广"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 订阅源列表 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                订阅源（可选，不选则搜索全部）
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1">
                {feedsLoading ? (
                  <div className="text-center py-4 text-sm text-gray-500">加载中...</div>
                ) : feeds.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">暂无订阅源</div>
                ) : (
                  feeds.map((feed) => (
                    <label
                      key={feed.id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={funnelSelectedFeeds.includes(feed.id)}
                        onChange={() => handleFeedCheckboxChange(feed.id)}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        disabled={addingFunnel}
                      />
                      <span className="text-sm text-gray-700 truncate flex-1">{feed.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* 错误提示 */}
            {funnelError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{funnelError}</p>
              </div>
            )}

            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseAddFunnelModal}
                disabled={addingFunnel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                取消
              </button>
              <button
                onClick={addFunnel}
                disabled={addingFunnel}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingFunnel ? '添加中...' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* 编辑漏斗弹窗 */}
      {showEditFunnelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseEditFunnelModal}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {/* 关闭按钮 */}
            <button
              onClick={handleCloseEditFunnelModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              编辑漏斗
            </h3>

            {/* 漏斗名称 */}
            <div className="mb-4">
              <label
                htmlFor="edit-funnel-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                漏斗名称
              </label>
              <input
                id="edit-funnel-name"
                type="text"
                value={funnelName}
                onChange={(e) => {
                  setFunnelName(e.target.value);
                  setFunnelError('');
                }}
                placeholder="例如：技术新闻"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 包含关键词 */}
            <div className="mb-4">
              <label
                htmlFor="edit-include-keywords"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                包含关键词（可选）
              </label>
              <input
                id="edit-include-keywords"
                type="text"
                value={funnelIncludeKeywords}
                onChange={(e) => {
                  setFunnelIncludeKeywords(e.target.value);
                  setFunnelError('');
                }}
                placeholder="多个关键词用逗号分隔，例如：AI,人工智能"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 不包含关键词 */}
            <div className="mb-4">
              <label
                htmlFor="edit-exclude-keywords"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                不包含关键词（可选）
              </label>
              <input
                id="edit-exclude-keywords"
                type="text"
                value={funnelExcludeKeywords}
                onChange={(e) => {
                  setFunnelExcludeKeywords(e.target.value);
                  setFunnelError('');
                }}
                placeholder="多个关键词用逗号分隔，例如：广告,推广"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }}
                disabled={addingFunnel}
              />
            </div>

            {/* 订阅源列表 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                订阅源（可选，不选则搜索全部）
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-1">
                {feedsLoading ? (
                  <div className="text-center py-4 text-sm text-gray-500">加载中...</div>
                ) : feeds.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">暂无订阅源</div>
                ) : (
                  feeds.map((feed) => (
                    <label
                      key={feed.id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={funnelSelectedFeeds.includes(feed.id)}
                        onChange={() => handleFeedCheckboxChange(feed.id)}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        disabled={addingFunnel}
                      />
                      <span className="text-sm text-gray-700 truncate flex-1">{feed.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* 错误提示 */}
            {funnelError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{funnelError}</p>
              </div>
            )}

            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCloseEditFunnelModal}
                disabled={addingFunnel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                取消
              </button>
              <button
                onClick={updateFunnel}
                disabled={addingFunnel}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingFunnel ? '保存中...' : '确认保存'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 删除漏斗确认弹窗 */}
      {showDeleteFunnelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelDeleteFunnel}
          />
          {/* 弹窗内容 */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {/* 标题 */}
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              确认删除漏斗
            </h3>
            {/* 确认信息 */}
            <p className="text-sm text-gray-600 mb-6">
              您确定要删除漏斗"{funnels.find(f => f.id === deletingFunnelId)?.name || '该漏斗'}"吗？
              删除后将无法恢复。
            </p>
            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDeleteFunnel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={confirmDeleteFunnel}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}